import { store } from './store.js';
// ========================================
// PASO 4: RAZONAMIENTO PEDAGÓGICO
// ========================================
import { wizardState, getString, extractQuestionFromStatement, isSubtraction } from './wizardState.js';
import { safeSetHTML } from './security.js';

export function initializeReasoningStep() {
    if (!store.state || !store.state.problems) return;

    const problem = store.state.problems[store.state.currentProblem];
    const usefulData = problem.data.useful;

    // Obtener nombres de elementos
    const objectNames = window.getAllObjectNames ? window.getAllObjectNames(problem) : ['unidades'];
    // Obtener el elemento de la pregunta
    const questionElement = window.getQuestionElement ? window.getQuestionElement(problem) : 'elementos';

    // Retrieve saved texts from Step 2
    const preservedTexts = wizardState.data.usefulDataTexts || [];

    // Fallback: extract phrases automatically from the problem statement
    const autoPhrases = window.getAllDataPhrases ? window.getAllDataPhrases(problem) : [];

    // Populate useful data list in intro text
    const dataList = usefulData.map((num, i) => {
        // Priority 1: Use preserved text from Step 2
        if (preservedTexts[i]) return preservedTexts[i];
        // Priority 2: Use auto-extracted phrase from problem statement
        if (autoPhrases[i]) return autoPhrases[i];
        // Priority 3: Basic fallback
        const elem = objectNames[i] || objectNames[0] || 'unidades';
        return `${num} ${elem}`;
    }).join(', ');

    const usefulDataListEl = document.getElementById('usefulDataList');
    if (usefulDataListEl) usefulDataListEl.textContent = dataList;

    // Store question text
    const questionText = extractQuestionFromStatement(problem);
    wizardState.data.questionText = questionText;
    wizardState.data.questionElement = questionElement;

    // Generar botones grandes para datos útiles
    const container = document.getElementById('largestDataButtons');
    const validateBtn = document.getElementById('validateLargestDataBtn');
    if (container) {
        safeSetHTML(container, '');
        if (validateBtn) validateBtn.style.display = 'none';
        
        wizardState.data.selectedLargestDataIndex = null; // Guardar selección
        
        const sortedIndices = usefulData
            .map((val, i) => ({ val: parseFloat(String(val).replace(',', '.').replace(/[^\d.-]/g, '')), i }))
            .sort((a, b) => b.val - a.val)
            .map(x => x.i);

        sortedIndices.forEach(i => {
            const num = usefulData[i];
            let optionText;
            if (preservedTexts[i]) optionText = preservedTexts[i];
            else if (autoPhrases[i]) optionText = autoPhrases[i];
            else {
                const elem = objectNames[i] || objectNames[0] || 'unidades';
                optionText = `${num} ${elem}`;
            }

            const btn = document.createElement('button');
            btn.className = 'btn btn-option-card largest-data-btn';
            btn.classList.add('btn-large-choice');
            btn.textContent = optionText;
            
            btn.onclick = () => {
                document.querySelectorAll('.largest-data-btn').forEach(b => b.classList.remove('selected'));
                btn.classList.add('selected');
                wizardState.data.selectedLargestDataIndex = i.toString();
                // Auto-advance
                setTimeout(() => { window.validateLargestData(); }, 600);
            };
            container.appendChild(btn);
        });
    }

    // Bind magnitude buttons
    const magButtons = document.querySelectorAll('.magnitude-btn');
    const magValBtn = document.getElementById('validateMagnitudeBtn');
    if (magValBtn) magValBtn.style.display = 'none';
    
    magButtons.forEach(btn => {
        btn.classList.remove('selected');
        btn.onclick = () => {
            magButtons.forEach(b => b.classList.remove('selected'));
            btn.classList.add('selected');
            wizardState.data.selectedMagnitude = btn.getAttribute('data-value');
            // Auto-advance
            setTimeout(() => { window.validateMagnitude(); }, 600);
        };
    });

    // Bind operation buttons
    const opButtons = document.querySelectorAll('.operation-btn');
    const opValBtn = document.getElementById('validateOperationBtn');
    if (opValBtn) opValBtn.style.display = 'none';

    opButtons.forEach(btn => {
        btn.classList.remove('selected');
        btn.onclick = () => {
            opButtons.forEach(b => b.classList.remove('selected'));
            btn.classList.add('selected');
            wizardState.data.selectedOperationButton = btn.getAttribute('data-value');
            // Auto-advance
            setTimeout(() => { window.validateOperation(); }, 600);
        };
    });

    // Reset substeps state
    wizardState.data.reasoning = {
        largestData: null,
        largestDataText: null,
        magnitude: null,
        operation: null,
        currentSubstep: 1
    };
    
    // UX-02: Reset progress
    if (typeof updateSubstepProgress === 'function') updateSubstepProgress(0);

    // Generate visual reminder of bars
    generateReasoningBarsReminder(usefulData, objectNames);

    // Elementos de subpasos
    const s1 = document.getElementById('substep1');
    const s2 = document.getElementById('substep2');
    const s4 = document.getElementById('substep4');
    if (s4) s4.classList.add('hidden');

    // NUEVA LÓGICA: Si los números son iguales, avisamos al usuario pero no saltamos el paso
    const allEqual = usefulData.every(val => val === usefulData[0]);

    if (allEqual && usefulData.length > 0) {
        Logger.info('✨ Datos iguales detectados. Avisando al usuario...');
        
        // Mostrar primer subpaso
        if (s1) s1.classList.remove('hidden');
        if (s2) s2.classList.add('hidden');
        
        // Mostrar el aviso con un pequeño retraso para que de tiempo a renderizar
        setTimeout(() => {
            window.showWizardFeedback('Como las dos cantidades son iguales, ¡puedes elegir cualquiera de las dos!', true);
        }, 800);

    } else {
        // Flujo normal: Mostrar primer subpaso
        if (s1) s1.classList.remove('hidden');
        if (s2) s2.classList.add('hidden');
    }

    Logger.info('🤔 Paso de razonamiento inicializado con datos:', usefulData);
}

// Helper para visualizar barras estáticas — siempre ordenadas de MAYOR a MENOR
function generateReasoningBarsReminder(dataValues, objectNames) {
    const container = document.getElementById('reasoningBarsReminder');
    if (!container) return;

    container.innerHTML = '';

    const step3Indicator = document.querySelector('.step-indicator[data-step="3"]');
    const step3Completed = step3Indicator ? step3Indicator.classList.contains('completed') : false;

    if (!step3Completed) {
        const warningMsg = getString('representationNotAvailable');
        safeSetHTML(container, `
            <div style="text-align: center; color: rgba(255, 255, 255, 0.6); font-weight: 500; padding: 12px 10px; font-size: 0.95rem; line-height: 1.4; width: 100%; box-sizing: border-box;">
                ${warningMsg}
            </div>
        `);
        return;
    }

    const maxVal = Math.max(...dataValues);

    // Ordenar de mayor a menor para que el número mayor aparezca SIEMPRE arriba
    const sortedPairs = dataValues
        .map((val, idx) => ({ val, idx }))
        .sort((a, b) => b.val - a.val);

    sortedPairs.forEach(({ val, idx }) => {
        const barDiv = document.createElement('div');
        barDiv.className = 'reasoning-mini-bar-row';
        barDiv.className = 'bar-div-container';

        const widthPct = (val / maxVal) * 80;
        const label = objectNames[idx] || 'unidades';

        safeSetHTML(barDiv, `
            <div style="width: 30px; font-weight: bold;">${val}</div>
            <div style="flex-grow: 1; background: #e2e8f0; height: 12px; border-radius: 6px; overflow: hidden;">
                <div style="width: ${widthPct}%; background: linear-gradient(90deg, #6366f1, #818cf8); height: 100%;"></div>
            </div>
            <div style="font-size: 0.8rem; color: #64748b; width: 80px; text-align: right;">${label}</div>
        `);
        container.appendChild(barDiv);
    });
}

// UX-02: Update progress indicators in Step 4
function updateSubstepProgress(completedSubstep) {
    for (let i = 1; i <= 3; i++) {
        const dot = document.querySelector(`.substep-dot[data-substep="${i}"]`);
        if (dot) {
            if (i <= completedSubstep) {
                dot.classList.add('completed');
            } else {
                dot.classList.remove('completed');
            }
        }
    }
}

// Sub-paso 1: Validar el mayor dato
export function validateLargestData() {
    Logger.info('[Step4] Validating Largest Data...');
    try {
        const problem = store.state.problems[store.state.currentProblem];
        const usefulData = problem.data.useful;
        const selectedIndex = parseInt(wizardState.data.selectedLargestDataIndex, 10);

        if (isNaN(selectedIndex) || selectedIndex < 0 || selectedIndex >= usefulData.length) {
            window.showWizardFeedback(getString('feedbackSelectNumber'), false);
            return;
        }

        const userSelection = parseFloat(String(usefulData[selectedIndex]).replace(',', '.').replace(/[^\d.-]/g, ''));

        // Ensure robust numeric comparison for usefulData
        const numericData = usefulData.map(n => parseFloat(String(n).replace(',', '.').replace(/[^\d.-]/g, ''))).filter(n => !isNaN(n));

        if (numericData.length === 0) {
            console.error('[Step4] No valid numeric data found');
            return;
        }

        const largest = Math.max(...numericData);
        const allEqual = numericData.length > 0 && numericData.every(val => val === numericData[0]);

        // Ensure errorCounts is defined defensively
        if (!wizardState.data.errorCounts) {
            wizardState.data.errorCounts = {};
        }

        // Initialize error tracking for step 4_1
        if (typeof wizardState.data.errorCounts.step4 === 'undefined') {
            wizardState.data.errorCounts.step4 = 0;
        }

        // Robust comparison (or accept if all are equal)
        if (allEqual || Math.abs(userSelection - largest) < 0.0001) {
            // Encontrar índice y nombre usando el seleccionado directamente
            const objectNames = window.getAllObjectNames ? window.getAllObjectNames(problem) : ['unidades'];
            const largestIndex = selectedIndex;
            const largestName = objectNames[largestIndex] || objectNames[0];

            // Ensure reasoning is defined defensively
            if (!wizardState.data.reasoning) {
                wizardState.data.reasoning = {};
            }

            wizardState.data.reasoning.largestData = largest;
            wizardState.data.reasoning.largestDataText = `${largest} ${largestName}`;

            wizardState.data.errorCounts.step4 = 0; // Reset on success

            // Feedback positivo
            window.showWizardFeedback(getString('feedbackCorrect'), true);

            // Hide Step 1, Show Step 2 (with animation)
            slideToSubstep('substep1', 'substep2');
            
            wizardState.data.reasoning.currentSubstep = 2;
            updateSubstepProgress(1); // UX-02: Mark substep 1 complete

            // Setup Step 2 text (Corrige IDs para coincidir con index.html)
            let qText = '';
            try {
                qText = wizardState.data.questionText || extractQuestionFromStatement(problem);
            } catch (e) {
                Logger.error('[Step4] Error extracting question text:', e);
                qText = '¿?';
            }

            const qReminder = document.getElementById('questionTextReminder');
            if (qReminder) qReminder.textContent = qText;

            // Nombre del elemento (e.g. "amigos")
            const qElement = wizardState.data.questionElement || 'elementos';
            const elName = document.getElementById('elementNameSubstep2');
            if (elName) elName.textContent = qElement;

            // Valor elegido (e.g. "24 amigos")
            const valDisplay = document.getElementById('selectedLargestValue');
            if (valDisplay) valDisplay.textContent = largest;

            // Valor para el siguiente select (si existe)
            const valDisplay2 = document.getElementById('confirmedLargestData2');
            if (valDisplay2) valDisplay2.textContent = largest;

            // Integrar el valor dentro de los botones de MAYOR/MENOR
            const valDisplayMayor = document.getElementById('magnitudeMaxTextMayor');
            if (valDisplayMayor) valDisplayMayor.textContent = largest;

            const valDisplayMenor = document.getElementById('magnitudeMaxTextMenor');
            if (valDisplayMenor) valDisplayMenor.textContent = largest;

            // Novedad: Recargar botones de voz porque acabamos de inyectar texto nuevo
            if (window.addSafeAudioButtons) {
                setTimeout(window.addSafeAudioButtons, 100);
            }

        } else {
            wizardState.data.errorCounts.step4 += 1;
            let msg = window.getFeedbackForAttempt ? 
                window.getFeedbackForAttempt('step4', wizardState.data.errorCounts.step4) : 
                getString('feedbackLookBars');
            
            if (window.DetectiveMentor && typeof window.DetectiveMentor.speak === 'function') {
                window.DetectiveMentor.speak(msg, 'warning', 6000);
            } else {
                window.showWizardFeedback(msg, false);
            }
        }
    } catch (err) {
        Logger.error('[Step4] Fatal error in validateLargestData:', err);
        // Fallback user feedback
        window.showWizardFeedback('Error al avanzar: ' + err.message, false);
    }
}

// Sub-paso 2: Validar Magnitud (Más vs Menos)
export function validateMagnitude() {
    // Get value from state (set by button clicks)
    const userValue = wizardState.data.selectedMagnitude;

    if (!userValue) {
        window.showWizardFeedback(getString('feedbackSelectOption') || 'Por favor, elige una opción', false);
        return;
    }

    const problem = store.state.problems[store.state.currentProblem];
    const expectedMagnitude = getExpectedMagnitude(problem); // Corrected argument

    if (userValue === expectedMagnitude) {
        // Ensure reasoning is defined defensively
        if (!wizardState.data.reasoning) {
            wizardState.data.reasoning = {};
        }
        wizardState.data.reasoning.magnitude = userValue;
        window.showWizardFeedback(getString('feedbackReasoningCorrect'), true);

        // Hide Step 2, Show Step 4 (with animation)
        slideToSubstep('substep2', 'substep4');

        wizardState.data.reasoning.currentSubstep = 4;
        updateSubstepProgress(2); // UX-02: Mark substep 2 complete
        
        // Novedad: Recargar botones de voz
        if (window.addSafeAudioButtons) {
            setTimeout(window.addSafeAudioButtons, 100);
        }
    } else {
        // Smart Feedback Integration
        if (window.SmartFeedback) {
            const opType = problem.operation === 'resta' ? 'resta' : 'suma';
            const analysis = SmartFeedback.analyzeMagnitudeError(expectedMagnitude, userValue, opType);

            SmartFeedback.trackError(analysis.type);
            const msg = SmartFeedback.getEnhancedMessage(analysis);
            window.showWizardFeedback(msg, false);
        } else {
            window.showWizardFeedback(getString('feedbackThinkAgain') || 'Piénsalo un poco mejor.', false);
        }
    }
}

// Sub-paso 4: Validar operación final
export function validateOperation() {
    const userOperation = wizardState.data.selectedOperationButton;

    if (!userOperation) {
        window.showWizardFeedback(getString('feedbackSelectOperation') || 'Elige Suma o Resta', false);
        return;
    }

    const problem = store.state.problems[store.state.currentProblem];
    const expectedOp = getExpectedOperation(problem);

    if (userOperation === expectedOp) {
        // Ensure reasoning is defined defensively
        if (!wizardState.data.reasoning) {
            wizardState.data.reasoning = {};
        }
        wizardState.data.reasoning.operation = userOperation;

        updateSubstepProgress(3); // UX-02: Mark substep 3 complete

        const msg = getString('feedbackOperationCorrect');
        window.showWizardFeedback(msg, true, true);
        window.markStepComplete(4);

        setTimeout(() => {
            window.routeNextWizardStep(4);
        }, 1500);
    } else {
        // Smart Feedback Integration
        if (window.SmartFeedback) {
            const analysis = SmartFeedback.analyzeOperationError(expectedOp, userOperation);

            SmartFeedback.trackError(analysis.type);
            const msg = SmartFeedback.getEnhancedMessage(analysis);
            window.showWizardFeedback(msg, false);
        } else {
            // Fallback (Logic kept for reference but redundant with SmartFeedback)
            let feedbackKey = 'feedbackThinkAgain';
            if (userOperation === 'suma' && expectedOp === 'resta') feedbackKey = 'feedbackWhySum';
            else if (userOperation === 'resta' && expectedOp === 'suma') feedbackKey = 'feedbackWhySub';

            window.showWizardFeedback(getString(feedbackKey), false);
        }
    }
}

function getExpectedMagnitude(problem) {
    if (isSubtraction(problem)) return 'menor';
    return 'mayor';
}

function getExpectedOperation(problem) {
    if (isSubtraction(problem)) return 'resta';
    return 'suma';
}

// ========================================
// FUNCIONES DE AYUDA (Expuestas globalmente para onclick del HTML)
// ========================================
window.showReasoningHelp = function () {
    const bubble = document.getElementById('reasoningHelpBubble');
    if (!bubble) return;

    // Actualizar valores dinámicos en la ayuda si existen
    const problem = store.state.problems[store.state.currentProblem];
    if (problem && problem.data.useful) {
        const maxVal = Math.max(...problem.data.useful);
        const helpVal1 = document.getElementById('helpLargestValue');
        const helpVal2 = document.getElementById('helpLargestValue2');

        if (helpVal1) helpVal1.textContent = maxVal;
        if (helpVal2) helpVal2.textContent = maxVal;
    }

    bubble.classList.remove('hidden');
    bubble.style.display = 'block'; // Asegurar visibilidad

    // Animación de entrada
    bubble.classList.add('bubble-anim-enter');
    // Forzar reflow para que la transición funcione
    void bubble.offsetWidth;
    bubble.classList.add('bubble-anim-active');
    bubble.classList.remove('bubble-anim-enter');
};

window.closeReasoningHelp = function () {
    const bubble = document.getElementById('reasoningHelpBubble');
    if (bubble) {
        bubble.classList.remove('bubble-anim-active');
        bubble.classList.add('bubble-anim-enter');
        setTimeout(() => {
            bubble.classList.add('hidden');
            bubble.style.display = 'none';
        }, 300);
    }
};

// ========================================
// FUNCIONES DE AUDIO
// ========================================

/**
 * Reproduce el audio de la introducción del razonamiento
 * @param {HTMLElement} btn - Botón que disparó la acción
 */
export function speakReasoningIntro(btn = null) {
    const dataList = document.getElementById('usefulDataList')?.textContent || '';
    const intro = `Ahora fíjate en el número mayor y piensa, el resultado será mayor o menor que ese número`;
    if (btn && window.toggleAudio) {
        window.toggleAudio(btn, intro);
    } else if (window.speak) {
        window.speak(intro, window.currentLanguage);
    }
}

/**
 * Reproduce el audio del sub-paso 2 del razonamiento  
 * @param {HTMLElement} btn - Botón que disparó la acción
 */
export function speakSubstep2Intro(btn = null) {
    const elementName = document.getElementById('elementNameSubstep2')?.textContent || 'elementos';
    const largestValue = document.getElementById('selectedLargestValue')?.textContent || '';
    const message = `Y ahora piensa y contesta, ¿Al final, el número de ${elementName} será mayor o menor que ${largestValue}?`;
    if (btn && window.toggleAudio) {
        window.toggleAudio(btn, message);
    } else if (window.speak) {
        window.speak(message, window.currentLanguage);
    }
}

/**
 * Reproduce el audio del recordatorio de la pregunta
 * @param {HTMLElement} btn - Botón que disparó la acción
 */
export function speakQuestionReminder(btn = null) {
    const qText = document.getElementById('questionTextReminder')?.textContent || wizardState.data.questionText || '';
    if (qText) {
        const message = `Recuerda la pregunta: ${qText}`;
        if (btn && window.toggleAudio) {
            window.toggleAudio(btn, message);
        } else if (window.speak) {
            window.speak(message, window.currentLanguage);
        }
    }
}

// ========================================
// ANIMACIONES
// ========================================
function slideToSubstep(fromId, toId) {
    const fromEl = document.getElementById(fromId);
    const toEl = document.getElementById(toId);
    if (!fromEl || !toEl) return;
    
    // Animate out
    fromEl.classList.add('animate-slide-out');
    
    setTimeout(() => {
        fromEl.classList.add('hidden');
        fromEl.classList.remove('animate-slide-out');
        
        // Animate in
        toEl.classList.remove('hidden');
        toEl.classList.add('animate-slide-in');
        
        setTimeout(() => {
            toEl.classList.remove('animate-slide-in');
        }, 400); // Wait for slide in
    }, 300); // Wait for slide out
}

